import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

// Animation cache to prevent duplicate animations
const animationCache = new WeakSet<HTMLElement>();

/**
 * Animates the newsletter section with optimized performance
 */
export const animateNewsletterSection = (
  sectionRef: RefType
): gsap.core.Timeline | undefined => {
  if (!sectionRef.current) {
    return;
  }

  // Prevent duplicate animations
  if (animationCache.has(sectionRef.current)) {
    return;
  }

  animationCache.add(sectionRef.current);

  // Get child elements for staggered animation
  const childElements = sectionRef.current.querySelectorAll("h2, p, form");

  const tl = gsap.timeline({
    scrollTrigger: {
      id: "newsletter-section",
      trigger: sectionRef.current,
      start: "top 85%",
      end: "bottom 15%",
      once: true,
      // Only enable in development for debugging
      markers: process.env.NODE_ENV === "development" ? false : false,
    },
  });

  // Set initial state
  gsap.set([sectionRef.current, childElements], {
    opacity: 0,
    y: 30,
  });

  // Animate container
  tl.to(sectionRef.current, {
    opacity: 1,
    duration: 0.1,
    ease: "none",
  });

  // Animate child elements with stagger
  tl.to(
    childElements,
    {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.15,
      ease: "power2.out",
    },
    0.1
  );

  return tl;
};

/**
 * Animates form submission states
 */
export const animateFormState = (
  formRef: RefType,
  state: "submitting" | "success" | "error" | "idle"
): void => {
  if (!formRef.current) return;

  const button = formRef.current.querySelector(
    'button[type="submit"]'
  ) as HTMLElement;
  const input = formRef.current.querySelector(
    'input[type="email"]'
  ) as HTMLElement;

  if (!button || !input) return;

  // Kill any existing animations
  gsap.killTweensOf([button, input]);

  switch (state) {
    case "submitting":
      gsap.to(button, {
        scale: 0.98,
        duration: 0.1,
        ease: "power2.out",
      });
      break;

    case "success":
      gsap
        .timeline()
        .to(button, {
          scale: 1.05,
          duration: 0.2,
          ease: "back.out(1.7)",
        })
        .to(button, {
          scale: 1,
          duration: 0.3,
          ease: "power2.out",
        });
      break;

    case "error":
      gsap
        .timeline()
        .to([button, input], {
          x: -5,
          duration: 0.1,
        })
        .to([button, input], {
          x: 5,
          duration: 0.1,
        })
        .to([button, input], {
          x: 0,
          duration: 0.1,
        });
      break;

    case "idle":
    default:
      gsap.to([button, input], {
        scale: 1,
        x: 0,
        duration: 0.3,
        ease: "power2.out",
      });
      break;
  }
};

/**
 * Animates success message appearance
 */
export const animateSuccessMessage = (messageRef: RefType): void => {
  if (!messageRef.current) return;

  gsap.fromTo(
    messageRef.current,
    {
      opacity: 0,
      y: -10,
      scale: 0.95,
    },
    {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.5,
      ease: "back.out(1.7)",
    }
  );
};

/**
 * Animates error message appearance
 */
export const animateErrorMessage = (messageRef: RefType): void => {
  if (!messageRef.current) return;

  gsap.fromTo(
    messageRef.current,
    {
      opacity: 0,
      x: -10,
    },
    {
      opacity: 1,
      x: 0,
      duration: 0.3,
      ease: "power2.out",
    }
  );
};

/**
 * Optimized cleanup function with better performance
 */
export const cleanupScrollTriggers = (): void => {
  // Only kill newsletter-specific triggers
  ScrollTrigger.getAll()
    .filter((trigger) => trigger.vars.id === "newsletter-section")
    .forEach((trigger) => trigger.kill());

  // Clear animation cache
  animationCache.clear?.();
};

/**
 * Refresh ScrollTrigger instances (useful for dynamic content)
 */
export const refreshScrollTriggers = (): void => {
  ScrollTrigger.refresh();
};
